import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";

import { columnFiltersToQueryParams, pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { useSystemConfigs } from "@/features/admin/api/use-system-configs";
import {
  SystemConfigEditDialog,
  systemConfigsColumns,
  type SystemConfigRow,
} from "@/features/admin/components";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";

/**
 * Clés de configuration système (`GET /SystemConfigs?page=&pageSize=` —
 * liste paginée, guide §6.8) — lecture authentifiée ; l'édition
 * (`PUT {key}` via ShareablePreview) est masquée aux rôles
 * flashpay-superadmin / flashpay-admin (RequireRole dans la colonne
 * d'actions).
 */
export function SystemConfigsPage() {
  const { t } = useAdminTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filters: columnFiltersToQueryParams(columnFilters),
    }),
    [pagination, columnFilters]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useSystemConfigs(params);
  const [editing, setEditing] = useState<SystemConfigRow | null>(null);

  const rows = useMemo(
    () => (data?.items ?? []).map((config) => ({ ...config, id: config.key })),
    [data]
  );

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("admin.configs.title")}
        </h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          {t("admin.configs.subtitle")}
        </p>
      </div>

      <DataTableClar
        data={rows}
        columnsClar={systemConfigsColumns(setEditing)}
        title={t("admin.configs.title")}
        exportFileName="system-configs"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <SystemConfigEditDialog
        config={editing}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
    </section>
  );
}
